/**
 * Digital Signage Screen 3 — Hero Slider (from /api/admin/hero)
 * Falls back to AvePark logo if no hero items exist
 * Matches site design: dark bg, Syne headings, gradient overlays, purple accent
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL = 5 * 60 * 1000;
const SLIDE_INTERVAL = 10000;

// Inline AvePark logo SVG (from Logo.jsx)
const AveParkLogo = () => (
  <svg viewBox="0 0 298.82 214.08" fill="#eeeded" style={{ width: 280, height: 'auto', opacity: 0.9 }}>
    <path d="M24.7,119.71v15c13.18,0,23.02,6.51,23.02,12.34s-9.85,12.33-23.02,12.33v15c21.32,0,38.02-12.01,38.02-27.33s-16.7-27.34-38.02-27.34Z"/>
    <path d="M110.06,118.38c-17.29,0-31.36,17.06-31.36,38.02v57.67h15v-57.67c0-12.48,7.49-23.02,16.36-23.02s16.36,10.54,16.36,23.02v57.67h15v-57.67c0-20.96-14.07-38.02-31.36-38.02Z"/>
    <rect x="101.66" y="159.38" width="16.8" height="15"/>
    <path d="M220.11,147.04c0-15.33-16.7-27.34-38.02-27.34v15c13.18,0,23.02,6.51,23.02,12.34s-9.84,12.33-23,12.34h-.02v15h.02c12.47.02,23.01,18.19,23.01,39.7h15c0-18.67-6.2-34.87-15.85-44.7,9.65-4.91,15.84-13.01,15.84-22.34Z"/>
    <rect x="236.1" y="119.68" width="15" height="94.39"/>
    <path d="M279.81,167.26c11.44-9.33,19.01-26.94,19.01-47.58h-15c0,21.52-10.54,39.7-23.02,39.7v.03c-.31,0-.61-.03-.92-.03v15c12.48,0,23.02,18.18,23.02,39.7h15c0-20.09-7.17-37.32-18.09-46.82Z"/>
    <path d="M120.34,62.17c-4.12,16.34-8.73,21.63-10.28,22.4-1.55-.77-6.16-6.06-10.28-22.4-3.92-15.56-6.08-35.91-6.08-57.31h-15c0,35.2,6.6,94.76,31.36,94.76s31.36-59.56,31.36-94.76h-15c0,21.4-2.16,41.75-6.08,57.31Z"/>
    <path d="M31.36,0C14.07,0,0,17.06,0,38.02v176.05h15V38.02c0-12.48,7.49-23.02,16.36-23.02s16.36,10.54,16.36,23.02v57.67h15v-57.67C62.72,17.06,48.65,0,31.36,0Z"/>
    <rect x="22.96" y="40.99" width="16.8" height="15"/>
    <path d="M182.09,4.89v15c13.18,0,23.02,6.51,23.02,12.34h15c0-15.33-16.7-27.34-38.02-27.34Z"/>
    <polygon points="157.4 99.26 157.4 214.07 172.4 214.07 172.4 99.26 172.4 4.86 157.4 4.86 157.4 99.26"/>
    <path d="M182.09,84.62v15c21.32,0,38.02-12.01,38.02-27.34h-15c0,5.83-9.84,12.34-23.02,12.34Z"/>
    <rect x="182.09" y="44.56" width="16.8" height="15"/>
  </svg>
);

const DSScreen3 = () => {
  const [heroes, setHeroes] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/hero`);
      if (res.data) setHeroes(res.data);
    } catch (e) {
      console.error('DS3 fetch:', e);
    }
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_INTERVAL); return () => clearInterval(i); }, []);

  useEffect(() => {
    if (heroes.length <= 1) return;
    const i = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIdx(prev => (prev + 1) % heroes.length); setFade(true); }, 600);
    }, SLIDE_INTERVAL);
    return () => clearInterval(i);
  }, [heroes.length]);

  const ls = (f) => { if (!f) return ''; if (typeof f === 'string') return f; return f.pl || f.en || Object.values(f)[0] || ''; };

  // No heroes — show logo fallback
  if (!heroes.length) {
    return (
      <div style={S.root}>
        <div style={S.logoGlow1} />
        <div style={S.logoGlow2} />
        <div style={S.logoCentre}>
          <AveParkLogo />
          <div style={S.logoTagline}>
            <div style={S.logoLine} />
            <span style={S.logoText}>WARSAW INDOOR SKATEPARK</span>
            <div style={S.logoLine} />
          </div>
        </div>
      </div>
    );
  }

  const hero = heroes[idx] || heroes[0];
  const mediaSrc = hero.mediaUrl ? (hero.mediaUrl.startsWith('/') ? `${API}${hero.mediaUrl}` : hero.mediaUrl) : null;
  const isVideo = hero.mediaType === 'video';

  return (
    <div style={S.root}>
      {/* Full-screen hero */}
      <div style={{ ...S.slideWrap, opacity: fade ? 1 : 0 }}>
        {/* Media */}
        {mediaSrc && !isVideo && (
          <img src={mediaSrc} alt="" style={S.media} />
        )}
        {mediaSrc && isVideo && (
          <video src={mediaSrc} autoPlay muted loop playsInline style={S.media} />
        )}

        {/* Gradient overlay */}
        <div style={S.overlay} />

        {/* Content */}
        <div style={S.heroContent}>
          {ls(hero.title) && (
            <h1 style={S.heroTitle}>{ls(hero.title)}</h1>
          )}
          {ls(hero.subtitle) && (
            <p style={S.heroSub}>{ls(hero.subtitle)}</p>
          )}
          {hero.buttons?.length > 0 && (
            <div style={S.heroBtns}>
              {hero.buttons.map((btn, i) => (
                <span key={i} style={i === 0 ? S.btnPrimary : S.btnSecondary}>
                  {ls(btn.text)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dots */}
      {heroes.length > 1 && (
        <div style={S.dots}>
          {heroes.map((_, i) => (
            <div key={i} style={{ ...S.dot, ...(i === idx ? S.dotActive : {}) }} />
          ))}
        </div>
      )}

      <style>{`@keyframes glow{0%,100%{opacity:0.2}50%{opacity:0.5}} @keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}`}</style>
    </div>
  );
};

const S = {
  root: { background: '#050505', width: '100vw', height: '100vh', overflow: 'hidden', fontFamily: "'Manrope', sans-serif", position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  // Logo fallback
  logoGlow1: { position: 'absolute', top: '30%', left: '40%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.15) 0%, transparent 70%)', pointerEvents: 'none', animation: 'glow 6s ease infinite' },
  logoGlow2: { position: 'absolute', bottom: '20%', right: '30%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(147,51,234,0.1) 0%, transparent 70%)', pointerEvents: 'none', animation: 'glow 8s ease infinite 2s' },
  logoCentre: { position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 40 },
  logoTagline: { display: 'flex', alignItems: 'center', gap: 20 },
  logoLine: { width: 60, height: 1, background: 'rgba(255,255,255,0.15)' },
  logoText: { color: 'rgba(255,255,255,0.3)', fontSize: 14, fontWeight: 600, letterSpacing: 6, fontFamily: "'Manrope', sans-serif" },

  // Hero slide
  slideWrap: { position: 'absolute', inset: 0, transition: 'opacity 0.6s ease' },
  media: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  overlay: { position: 'absolute', inset: 0, background: 'linear-gradient(0deg, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.5) 40%, rgba(5,5,5,0.15) 70%, rgba(5,5,5,0.1) 100%)' },
  heroContent: { position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '0 80px 80px', maxWidth: '60%' },
  heroTitle: { color: '#fff', fontSize: 64, fontWeight: 900, fontFamily: "'Syne', sans-serif", lineHeight: 1.05, letterSpacing: -2, textShadow: '0 4px 40px rgba(0,0,0,0.5)' },
  heroSub: { color: 'rgba(255,255,255,0.6)', fontSize: 22, marginTop: 20, lineHeight: 1.5, textShadow: '0 2px 20px rgba(0,0,0,0.5)' },
  heroBtns: { display: 'flex', gap: 16, marginTop: 32 },
  btnPrimary: { display: 'inline-block', background: 'linear-gradient(135deg, #7C3AED, #9333EA)', color: '#fff', padding: '14px 36px', borderRadius: 50, fontSize: 16, fontWeight: 700, letterSpacing: 0.5 },
  btnSecondary: { display: 'inline-block', background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '14px 36px', borderRadius: 50, fontSize: 16, fontWeight: 600, border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' },

  // Dots
  dots: { position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 10, zIndex: 10 },
  dot: { width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', transition: 'all 0.4s' },
  dotActive: { background: '#7C3AED', width: 28, borderRadius: 4 },
};

export default DSScreen3;
